<template>
    <div class="product-selection-page">
        <!-- Header -->
        <v-card class="elevation-4 mb-4" style="border-radius: 16px; overflow: hidden;">
            <v-card-title class="teal darken-2 white--text pa-5" style="background: linear-gradient(135deg, #00897B 0%, #00695C 100%);">
                <v-btn icon dark @click="goBack" large class="mr-3">
                    <v-icon>arrow_back</v-icon>
                </v-btn>
                <v-icon left color="white" size="36">shopping_basket</v-icon>
                <span class="headline font-weight-bold">Select a Product to Save For</span>
            </v-card-title>

            <!-- Message d'information -->
            <v-card-text class="pa-4">
                <v-alert
                    type="info"
                    color="blue darken-1"
                    icon="savings"
                    class="elevation-2"
                    dense
                    prominent
                    style="border-radius: 12px;"
                >
                    <div class="font-weight-bold">💰 Smart Savings Campaign</div>
                    <div class="caption mt-1">Choose a product and we'll automatically create a personalized savings plan to help you buy it!</div>
                </v-alert>
            </v-card-text>
        </v-card>

        <!-- Filtres et recherche -->
        <v-card class="mb-4 pa-4 elevation-3" style="border-radius: 12px; background: white;">
            <v-layout row wrap align-center>
                <v-flex xs12 sm6 md6 class="pr-2">
                    <v-select
                        @change="getProducts()"
                        class="pt-0 mt-0"
                        color="teal"
                        v-model="productCategory"
                        item-text="name"
                        dense
                        item-value="id"
                        :items="productCategories"
                        label="Filter by Category"
                        prepend-inner-icon="filter_list"
                        outlined
                        hide-details
                    ></v-select>
                </v-flex>

                <v-flex xs10 sm5 md5 class="pl-1">
                    <v-text-field
                        v-model="searchQuery"
                        class="pt-0 mt-0"
                        label="Search products..."
                        dense
                        clearable
                        prepend-inner-icon="search"
                        outlined
                        hide-details
                        @keyup.enter="searchProducts()"
                    ></v-text-field>
                </v-flex>
                <v-flex xs2 sm1 md1 class="pl-2">
                    <v-btn fab small color="teal darken-2" @click="searchProducts()" class="elevation-4">
                        <v-icon small class="white--text">search</v-icon>
                    </v-btn>
                </v-flex>
            </v-layout>
        </v-card>

        <!-- Loader -->
        <div v-if="loadingProducts" class="text-xs-center pa-5">
            <v-progress-circular
                indeterminate
                color="teal darken-2"
                :size="60"
                :width="5"
            ></v-progress-circular>
            <p class="mt-4 title grey--text">Loading amazing products...</p>
        </div>

        <!-- Grille de produits -->
        <v-layout row wrap v-else-if="products.length > 0">
            <v-flex
                xs12
                sm6
                md4
                lg3
                v-for="product in products"
                :key="product.id"
                class="pa-2"
            >
                <v-hover>
                    <v-card
                        slot-scope="{ hover }"
                        :class="[
                            `elevation-${hover ? 12 : 2}`,
                            { 'selected-product-card': selectedProduct && selectedProduct.id === product.id }
                        ]"
                        @click="selectProduct(product)"
                        style="border-radius: 12px; height: 100%; cursor: pointer; overflow: hidden; transition: all 0.3s ease;"
                    >
                        <v-container fluid class="pa-0">
                            <v-layout column class="ma-0 pa-0">
                                <!-- Section image -->
                                <v-flex class="pa-0 ma-0">
                                    <div class="media-container" style="position: relative; width: 100%; height: 260px; background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%); overflow: hidden;">
                                        <!-- Vidéo -->
                                        <video
                                            v-if="isVideo(product.get_image)"
                                            :src="product.get_image"
                                            style="width: 100%; height: 100%; object-fit: cover;"
                                            muted
                                        >
                                        </video>

                                        <!-- Image -->
                                        <v-img
                                            v-else
                                            :src="product.get_image"
                                            height="260px"
                                            width="100%"
                                            style="transition: transform 0.4s ease;"
                                            :style="hover ? 'transform: scale(1.1)' : ''"
                                            cover
                                        >
                                            <template v-slot:placeholder>
                                                <v-layout fill-height align-center justify-center ma-0>
                                                    <v-progress-circular indeterminate color="teal"></v-progress-circular>
                                                </v-layout>
                                            </template>
                                        </v-img>

                                        <!-- Badge "In Stock" -->
                                        <div v-if="product.quantity > 0" style="position: absolute; top: 12px; right: 12px; background: rgba(76, 175, 80, 0.95); color: white; padding: 6px 12px; border-radius: 20px; font-size: 11px; font-weight: bold; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
                                            <v-icon small color="white" style="font-size: 14px;">check_circle</v-icon>
                                            In Stock: {{ product.quantity }}
                                        </div>

                                        <!-- Badge "Out of Stock" -->
                                        <div v-else style="position: absolute; top: 12px; right: 12px; background: rgba(244, 67, 54, 0.95); color: white; padding: 6px 12px; border-radius: 20px; font-size: 11px; font-weight: bold; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
                                            <v-icon small color="white" style="font-size: 14px;">warning</v-icon>
                                            Out of Stock
                                        </div>

                                        <!-- Overlay sélection -->
                                        <div
                                            v-if="selectedProduct && selectedProduct.id === product.id"
                                            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 137, 123, 0.85); display: flex; flex-direction: column; align-items: center; justify-content: center; animation: fadeIn 0.3s ease;"
                                        >
                                            <v-icon size="72" color="white" style="animation: scaleIn 0.3s ease;">check_circle</v-icon>
                                            <div class="white--text mt-3 font-weight-bold" style="font-size: 18px; text-shadow: 0 2px 4px rgba(0,0,0,0.3);">SELECTED</div>
                                        </div>

                                        <!-- Hover overlay subtil -->
                                        <div
                                            v-if="!isVideo(product.get_image) && hover && !(selectedProduct && selectedProduct.id === product.id)"
                                            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.1); display: flex; align-items: center; justify-content: center; transition: all 0.3s;"
                                        >
                                            <v-icon size="48" color="white" style="text-shadow: 0 2px 4px rgba(0,0,0,0.5);">touch_app</v-icon>
                                        </div>
                                    </div>
                                </v-flex>

                                <!-- Informations produit -->
                                <v-flex class="ma-0 pa-3 text-xs-left" style="width: 100%; background: white;">
                                    <div>
                                        <div class="title ma-0 pa-0 text-xs-left mb-2" style="font-size: 16px; line-height: 1.3;">
                                            <strong>{{ product.name | truncate(30, '...') }}</strong>
                                        </div>

                                        <v-chip small color="blue lighten-5" text-color="blue darken-3" class="mb-2">
                                            <v-icon x-small left>category</v-icon>
                                            {{ product.category }}
                                        </v-chip>

                                        <div class="mb-2">
                                            <div class="caption grey--text mb-1">Description:</div>
                                            <div class="body-2" style="line-height: 1.4;">
                                                {{ getCleanDescription(product.description, 70) }}
                                            </div>
                                        </div>

                                        <v-divider class="my-2"></v-divider>

                                        <v-layout row wrap align-center>
                                            <v-flex xs12 class="mb-2">
                                                <div class="text-xs-center pa-2" style="background: linear-gradient(135deg, #00897B 0%, #00695C 100%); border-radius: 8px;">
                                                    <div class="caption white--text mb-1" style="opacity: 0.9;">Price</div>
                                                    <div class="title white--text font-weight-bold" style="font-size: 22px;">
                                                        {{ product.price }} FCFA
                                                    </div>
                                                </div>
                                            </v-flex>
                                        </v-layout>
                                    </div>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-card>
                </v-hover>
            </v-flex>
        </v-layout>

        <!-- Aucun produit -->
        <v-card v-else class="text-xs-center pa-5 elevation-2" style="border-radius: 12px;">
            <v-icon size="80" color="grey lighten-1" class="mb-3">shopping_basket</v-icon>
            <h3 class="grey--text mb-2">No Products Found</h3>
            <p class="grey--text body-1">Try adjusting your filters or search with different keywords.</p>
        </v-card>

        <!-- Barre d'action flottante -->
        <v-footer
            fixed
            class="elevation-24 bottom-action-bar"
            style="background: white; border-top: 4px solid #00897B; z-index: 9999 !important; height: auto !important; padding: 20px 0;"
        >
            <v-container fluid class="pa-4">
                <v-layout align-center justify-space-between row wrap>
                    <v-flex xs12 sm7 md8 class="mb-3 mb-sm-0">
                        <v-layout align-center>
                            <!-- Miniature du produit -->
                            <v-flex shrink class="mr-3" v-if="selectedProduct">
                                <v-avatar size="70" tile style="border-radius: 10px; border: 3px solid #00897B; box-shadow: 0 4px 8px rgba(0,0,0,0.1);">
                                    <v-img
                                        v-if="!isVideo(selectedProduct.get_image)"
                                        :src="selectedProduct.get_image"
                                        cover
                                    ></v-img>
                                    <v-icon v-else color="teal darken-2" size="40">shopping_basket</v-icon>
                                </v-avatar>
                            </v-flex>

                            <!-- Informations -->
                            <v-flex grow>
                                <div v-if="selectedProduct">
                                    <div class="caption grey--text text--darken-1 mb-1">
                                        <v-icon small color="teal darken-2" class="mr-1">check_circle</v-icon>
                                        SELECTED PRODUCT
                                    </div>
                                    <div class="subtitle-1 font-weight-bold mb-1" style="line-height: 1.3;">
                                        {{ selectedProduct.name | truncate(50, '...') }}
                                    </div>
                                    <div class="title teal--text text--darken-2 font-weight-bold">
                                        {{ selectedProduct.price }} FCFA
                                    </div>
                                </div>
                                <div v-else class="body-1 grey--text text--darken-1">
                                    <v-icon color="grey darken-1" class="mr-2">info</v-icon>
                                    Please select a product to continue
                                </div>
                            </v-flex>
                        </v-layout>
                    </v-flex>

                    <!-- Boutons -->
                    <v-flex xs12 sm5 md4 class="text-xs-right text-sm-right">
                        <v-btn
                            color="teal darken-2"
                            dark
                            @click="confirmProductSelection"
                            :disabled="!selectedProduct"
                            x-large
                            class="px-8 elevation-8"
                            style="border-radius: 28px; text-transform: none; font-weight: bold; min-height: 56px;"
                        >
                            <v-icon left>arrow_forward</v-icon>
                            <span style="font-size: 16px;">Create Savings Campaign</span>
                        </v-btn>
                    </v-flex>
                </v-layout>
            </v-container>
        </v-footer>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            loadingProducts: false,
            products: [],
            selectedProduct: null,
            productCategory: '',
            productCategories: [],
            searchQuery: '',
        }
    },

    filters: {
        truncate(text, length, suffix) {
            if (!text) return ''
            const plainText = text.replace(/<[^>]*>/g, '')
            if (plainText.length > length) {
                return plainText.substring(0, length) + suffix
            } else {
                return plainText
            }
        }
    },

    methods: {
        getCleanDescription(description, maxLength) {
            if (!description) return ''
            const cleanText = description.replace(/<[^>]*>/g, '').trim()
            if (cleanText.length > maxLength) {
                return cleanText.substring(0, maxLength) + '...'
            }
            return cleanText
        },

        isVideo(url) {
            if (!url) return false
            const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.wmv', '.flv', '.mkv']
            const lowerUrl = url.toLowerCase()
            return videoExtensions.some(ext => lowerUrl.includes(ext))
        },

        goBack() {
            this.$router.go(-1)
        },

        async loadProductCategories() {
            try {
                const response = await axios.get('/api/v1/market_place/get/products/categories/')
                this.productCategories = response.data
                if (this.productCategories.length > 0) {
                    this.productCategory = this.productCategories[0].id
                }
            } catch (error) {
                console.error('Error loading categories:', error)
                this.$store.commit('setSnackBarMessage', 'Error loading categories')
                this.$store.commit('setSnackBarColor', 'red darken-4')
                this.$store.commit('activateSnackBar', true)
            }
        },

        async getProducts() {
            this.loadingProducts = true
            try {
                const response = await axios.get(
                    `/api/v1/market_place/get/products/${this.productCategory}/all/info/`
                )
                this.products = response.data
            } catch (error) {
                console.error('Error loading products:', error)
                this.$store.commit('setSnackBarMessage', 'Error loading products')
                this.$store.commit('setSnackBarColor', 'red darken-4')
                this.$store.commit('activateSnackBar', true)
            } finally {
                this.loadingProducts = false
            }
        },

        async searchProducts() {
            if (!this.searchQuery || this.searchQuery.trim() === '') {
                await this.getProducts()
                return
            }

            this.loadingProducts = true
            try {
                const response = await axios.get(
                    `/api/v1/market_place/get/products/${this.productCategory}/all/${this.searchQuery}/info/`
                )
                this.products = response.data
            } catch (error) {
                console.error('Error searching products:', error)
                this.$store.commit('setSnackBarMessage', 'Error searching products')
                this.$store.commit('setSnackBarColor', 'red darken-4')
                this.$store.commit('activateSnackBar', true)
            } finally {
                this.loadingProducts = false
            }
        },

        selectProduct(product) {
            this.selectedProduct = product
            console.log('Product selected:', product)
        },

        confirmProductSelection() {
            if (!this.selectedProduct) {
                this.$store.commit('setSnackBarMessage', 'Please select a product')
                this.$store.commit('setSnackBarColor', 'orange darken-2')
                this.$store.commit('activateSnackBar', true)
                return
            }

            // Sauvegarder le produit sélectionné dans le store
            this.$store.commit('setSaveItem', this.selectedProduct.id)

            // Message de confirmation
            this.$store.commit('setSnackBarMessage', `Campaign for "${this.selectedProduct.name}" is being created...`)
            this.$store.commit('setSnackBarColor', 'teal darken-2')
            this.$store.commit('activateSnackBar', true)

            // Rediriger vers la page de création de campagne
            this.$router.push('/market_place/campaign/create/new_campaign')
        },
    },

    async mounted() {
        document.title = "PettyCash | Select Product for Campaign"
        this.$store.commit('setIsLoading', true)

        await this.loadProductCategories()
        await this.getProducts()

        this.$store.commit('setIsLoading', false)
    },
}
</script>

<style scoped>
.product-selection-page {
    padding: 20px;
    padding-bottom: 140px; /* Espace pour la barre de sélection */
    min-height: 100vh;
}

.bottom-action-bar {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    z-index: 1000 !important;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15) !important;
}

.selected-product-card {
    border: 4px solid #00897B !important;
    background: linear-gradient(to bottom, #e8f5f3, #ffffff) !important;
    box-shadow: 0 8px 24px rgba(0, 137, 123, 0.3) !important;
    transform: scale(1.02);
}

.v-card {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 28px rgba(0,0,0,0.15) !important;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes scaleIn {
    from {
        transform: scale(0.5);
    }
    to {
        transform: scale(1);
    }
}

/* Animation de la barre flottante */
.v-footer {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>